import { Link } from 'react-router-dom'
import { categoryLabel, useLocale } from '../../i18n/locale'
import type { PredictionViewModel } from '../../types/presentation'
import { PercentageValue } from '../ui/PercentageValue'
import { ProbabilityBar } from '../ui/ProbabilityBar'

interface EventCardProps {
  prediction: PredictionViewModel
  variant?: 'feature' | 'spotlight' | 'movement' | 'compact'
}
function changeClass(direction: PredictionViewModel['changeDirection']) {
  if (direction === 'up') return 'change-positive'
  if (direction === 'down') return 'change-negative'
  return 'change-neutral'
}

function changeGlyph(direction: PredictionViewModel['changeDirection']) {
  if (direction === 'up') return '↑'
  if (direction === 'down') return '↓'
  return '—'
}

function eventTypeLabel(prediction: PredictionViewModel, t: ReturnType<typeof useLocale>['t']) {
  if (prediction.eventType === 'multiOutcome') return t('card.outcomeMulti')
  if (prediction.eventType === 'range') return t('card.outcomeRange')
  if (prediction.eventType === 'threshold') return t('card.outcomeThreshold')
  if (prediction.eventType === 'series') return t('card.outcomeSeries')
  return undefined
}

export function EventCard({ prediction, variant = 'compact' }: EventCardProps) {
  const { locale, t } = useLocale()
  const isBinary = prediction.isBinary
  const typeLabel = eventTypeLabel(prediction, t)
  const change = prediction.change24h
  const visibleOutcomes = isBinary ? [] : prediction.outcomes.slice(0, 4)

  return (
    <article
      className={`prediction-card event-card prediction-card-${variant} presentation-card`}
      data-event-id={prediction.eventId}
      data-display-title={prediction.displayTitle}
      data-display-semantic-key={prediction.displaySemanticKey}
      data-outcome-structure={prediction.outcomeStructure}
    >
      <Link className="event-card-link" to={`/event/${encodeURIComponent(prediction.eventId)}`} aria-label={t('card.view', { title: prediction.displayTitle })}>
        <div className="prediction-card-head">
          <span className={`category-tag category-tag-${prediction.category}`}>{categoryLabel(prediction.category, locale)}</span>
          {typeLabel && <span className="event-type-tag">{typeLabel}</span>}
          {prediction.temporalStatus === 'ended' && <span className="event-type-tag">{t('card.ended')}</span>}
          <span className="source-chip event-source-chip">{prediction.sourceSummary}</span>
        </div>

        {isBinary ? (
          <div className="prediction-card-probability presentation-probability">
            <span className="presentation-label">{t('card.currentJudgement')}</span>
            {prediction.probability === undefined ? <strong className="probability-missing">{t('card.noProbability')}</strong> : <PercentageValue value={prediction.probability} element="strong" />}
            <span className="probability-interpretation">{prediction.probabilityInterpretation}</span>
          </div>
        ) : (
          <div className="prediction-card-distribution" aria-label={prediction.outcomeStructureLabel}>
            <span className="distribution-label">{prediction.outcomeStructureLabel}</span>
            <div className="distribution-values">
              {visibleOutcomes.map((outcome) => (
                <span className="distribution-value" key={outcome.id}>
                  {outcome.probability === undefined ? <strong className="probability-missing">{t('card.noProbability')}</strong> : <PercentageValue value={outcome.probability} element="strong" />}
                  <small>{outcome.displayOutcomeLabel}</small>
                </span>
              ))}
            </div>
            {prediction.outcomeStructureNote && <p className="distribution-note">{prediction.outcomeStructureNote}</p>}
          </div>
        )}

        <div className="prediction-card-title-row">
          <span className="prediction-title-label">{t('card.happens')}</span>
          <h3>{prediction.displayTitle}</h3>
        </div>

        <div className="presentation-reason-block">
          <span className="presentation-label">{t('card.todayWhy')}</span>
          <p>{prediction.todayReason ?? t('card.noVerifiedChange')}</p>
        </div>

        <div className="presentation-reason-block presentation-importance-block">
          <span className="presentation-label">{t('card.importance')}</span>
          <p>{prediction.importanceReason}</p>
        </div>

        <div className="prediction-card-change-row presentation-change-row">
          <span className={`change-value ${changeClass(prediction.changeDirection)}`}>
            <span aria-hidden="true">{changeGlyph(prediction.changeDirection)}</span>{' '}
            {change === undefined ? t('card.changeNone') : prediction.changeLabel}
          </span>
          {change !== undefined && <span className="change-caption">{t('card.last24h')}</span>}
          {prediction.consensusLabel && <span className="event-consensus-label">{prediction.consensusLabel}</span>}
        </div>

        {isBinary && prediction.probability !== undefined && <ProbabilityBar probability={prediction.probability} />}

        <div className="prediction-card-stats presentation-card-stats">
          <span><b>{t('card.source')}</b>{prediction.sourceSummary}</span>
          <span><b>{t('card.deadline')}</b>{prediction.deadlineLabel}</span>
          {prediction.relatedEventCount > 0
            ? <span><b>{t('card.related')}</b>{t('card.relatedCount', { count: prediction.relatedEventCount })}</span>
            : <span><b>{t('card.probabilityMeaning')}</b>{t('card.marketJudgement')}</span>}
        </div>
      </Link>
    </article>
  )
}
